import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
  HttpEvent,
  HttpHandlerFn,
} from '@angular/common/http'
import { inject } from '@angular/core'
import { Observable, throwError, catchError, tap } from 'rxjs'
import { HttpErrorHandlerService } from '../services/http-error-handler.service'
import { environment } from 'src/environments/environment'

const DEBUG = environment.logLevel === 'debug'

// Custom header to mark requests that were successfully retried
export const RETRY_SUCCESS_HEADER = 'X-Retry-Succeeded'

// Check if error is "too many requests" (could be 429 or 500 with specific message)
function isTooManyRequestsError(error: HttpErrorResponse): boolean {
  if (error.status === 429) return true

  // Check for 500 errors that are actually "too many requests" from Cognito
  if (error.status === 500 && error.error?.message) {
    const errorMsg = error.error.message.toLowerCase()
    return (
      errorMsg.includes('too many requests') ||
      errorMsg.includes('rate exceeded')
    )
  }

  return false
}

export const retryInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const errorHandler = inject(HttpErrorHandlerService)

  // Skip retry for specific endpoints if needed
  const skipRetryPaths = [
    '/logout',
    '/register', // User registration
    '/confirm', // Email confirmation
    '/resend-code', // Resend confirmation code
    '/forgot-password', // Password reset request
    '/reset-password', // Password reset confirmation
    '/auth/register', // Full auth registration path
    '/auth/confirm', // Full auth confirmation path
    '/auth/resend-code', // Full auth resend code path
    '/auth/forgot-password', // Full auth forgot password path
    '/auth/reset-password', // Full auth reset password path
    'auth/invite', // User invitation endpoints
  ]
  if (skipRetryPaths.some((path) => req.url.includes(path))) {
    return next(req)
  }

  // Track request start time
  const startTime = Date.now()
  const maxElapsedTime = 13000 // 13 seconds max retry window
  const maxRetries = 10
  let retryCount = 0

  // Initial backoff time
  const initialBackoff = 100 // 100ms

  // Add request identifier for better logging
  const requestId = Math.floor(Math.random() * 10000)

  if (DEBUG)
    console.log(
      `🚀 [Retry-${requestId}] Initial request: ${req.method} ${req.url}`,
    )

  return next(req).pipe(
    tap((response) => {
      if (DEBUG)
        console.log(
          `✅ [Retry-${requestId}] Request successful on first attempt: ${req.method} ${req.url}`,
        )
    }),
    catchError((error: HttpErrorResponse) => {
      // Only retry if status code is 400 or higher
      if (error.status < 400) {
        if (DEBUG)
          console.log(
            `⏩ [Retry-${requestId}] Not retrying - Status code ${error.status} is less than 400`,
          )
        return throwError(() => error)
      }

      // Special handling for "too many requests" errors
      const isTooManyRequests = isTooManyRequestsError(error)
      if (isTooManyRequests) {
        if (DEBUG)
          console.log(
            `🔄 [Retry-${requestId}] "Too Many Requests" error detected (status: ${error.status}). Will retry with priority.`,
          )
      } else {
        if (DEBUG)
          console.log(
            `❌ [Retry-${requestId}] Request failed with status ${error.status}: ${req.method} ${req.url}`,
          )
      }

      // For login endpoints, check for invalid credential errors and don't retry
      if (req.url.includes('/login')) {
        // Always skip retry for 401 errors on login (invalid credentials)
        if (error.status === 401) {
          if (DEBUG)
            console.log(
              `🔒 [Retry-${requestId}] Skipping retry for login 401 (invalid credentials): ${req.method} ${req.url}`,
            )
          return throwError(() => error)
        }

        // Also check error message for other credential-related errors
        if (error.error?.message) {
          const errorMsg = error.error.message.toLowerCase()
          if (
            errorMsg.includes('invalid username') ||
            errorMsg.includes('invalid password') ||
            errorMsg.includes('invalid credentials') ||
            errorMsg.includes('incorrect username') ||
            errorMsg.includes('incorrect password') ||
            errorMsg.includes('user not found') ||
            errorMsg.includes('authentication failed') ||
            errorMsg.includes('unauthorized') ||
            errorMsg.includes('user_not_confirmed') // Unconfirmed user
          ) {
            if (DEBUG)
              console.log(
                `🔒 [Retry-${requestId}] Skipping retry for login credential error: "${error.error.message}"`,
              )
            return throwError(() => error)
          }
        }

        // Also check for specific error codes that login component handles
        if (error.error?.error === 'USER_NOT_CONFIRMED') {
          if (DEBUG)
            console.log(
              `🔒 [Retry-${requestId}] Skipping retry for USER_NOT_CONFIRMED error`,
            )
          return throwError(() => error)
        }
      }

      // Check if we've exceeded retry attempts or time window
      const elapsedTime = Date.now() - startTime
      if (retryCount >= maxRetries || elapsedTime > maxElapsedTime) {
        const reason =
          retryCount >= maxRetries
            ? `Exceeded max retry attempts (${maxRetries})`
            : `Exceeded max retry window (${maxElapsedTime}ms)`

        const errorInfo = errorHandler.getDiagnosticErrorMessage(
          error,
          `${req.method} ${req.url} - ${reason}`,
        )
        // SUPER OBVIOUS FAILURE LOG
        console.error(
          `\n\n` +
            `████████████████████████████████████████████████████████████████████████\n` +
            `🛑🛑🛑 [Retry-${requestId}] FINAL FAILURE: ${req.method} ${req.url} 🛑🛑🛑\n` +
            `Reason: ${reason}\n` +
            `Status: ${error.status} | Message: ${errorInfo.userMessage}\n` +
            `████████████████████████████████████████████████████████████████████████\n\n`,
        )
        return throwError(() => error)
      }

      // Calculate backoff time with exponential strategy
      // For "too many requests" errors, use longer backoff
      let backoffTime = initialBackoff * Math.pow(2, retryCount)
      if (isTooManyRequests) {
        // Add random jitter plus extra time for rate limit errors
        backoffTime = backoffTime * 1.5 + Math.random() * 500
      }

      // Increment retry count and log
      retryCount++
      if (DEBUG)
        console.log(
          `🔄 [Retry-${requestId}] Attempt ${retryCount}/${maxRetries} after ${backoffTime}ms delay: ${req.method} ${req.url}`,
        )
      if (DEBUG)
        console.log(
          `   [Retry-${requestId}] Status: ${error.status}, Error: ${error.message}`,
        )

      // Return the error wrapped in an observable that will retry after delay
      return new Observable<HttpEvent<unknown>>((observer) => {
        setTimeout(() => {
          if (DEBUG)
            console.log(
              `🔄 [Retry-${requestId}] Executing retry attempt ${retryCount}...`,
            )

          next(req).subscribe({
            next: (value) => {
              if (DEBUG)
                console.log(
                  `✅ [Retry-${requestId}] Retry successful on attempt ${retryCount}!`,
                )

              // Mark this response as a successful retry to prevent error toast
              // We need to check the actual type of value to modify it
              if ('headers' in value && typeof value.clone === 'function') {
                // It's a HttpResponse, use clone to add our custom header
                const modifiedResponse = value.clone({
                  headers: value.headers.set(RETRY_SUCCESS_HEADER, 'true'),
                })
                observer.next(modifiedResponse)
              } else {
                // For other types of events, pass through
                observer.next(value)
              }

              observer.complete()
            },
            error: (retryError) => {
              // Check if we can retry again
              const newElapsedTime = Date.now() - startTime
              if (retryCount >= maxRetries || newElapsedTime > maxElapsedTime) {
                // Can't retry anymore, pass the error through
                const reason =
                  retryCount >= maxRetries
                    ? `max attempts (${maxRetries})`
                    : `time window (${maxElapsedTime}ms, actual: ${newElapsedTime}ms)`
                // SUPER OBVIOUS FAILURE LOG
                console.error(
                  `\n\n` +
                    `████████████████████████████████████████████████████████████████████████\n` +
                    `🛑🛑🛑 [Retry-${requestId}] FINAL FAILURE: ${req.method} ${req.url} 🛑🛑🛑\n` +
                    `Reason: ${reason}\n` +
                    `Status: ${(retryError && retryError.status) || 'unknown'} | Message: ${(retryError && retryError.message) || 'unknown'}\n` +
                    `████████████████████████████████████████████████████████████████████████\n\n`,
                )
                observer.error(retryError)
              } else {
                // Let the catchError handle it again
                if (DEBUG)
                  console.log(
                    `❌ [Retry-${requestId}] Retry attempt ${retryCount} failed. Will try again.`,
                  )
                observer.error(retryError)
              }
            },
          })
        }, backoffTime)
      }).pipe(
        catchError((retryError: HttpErrorResponse) => {
          // This is reached when we decide not to retry anymore
          console.error(
            `\n\n` +
              `████████████████████████████████████████████████████████████████████████\n` +
              `🛑🛑🛑 [Retry-${requestId}] FINAL FAILURE: ${req.method} ${req.url} 🛑🛑🛑\n` +
              `Status: ${(retryError && retryError.status) || 'unknown'} | Message: ${(retryError && retryError.message) || 'unknown'}\n` +
              `████████████████████████████████████████████████████████████████████████\n\n`,
          )
          return throwError(() => retryError)
        }),
      )
    }),
  )
}
